/**
 * Menu structure and permission ids for data-driven nav and route guards.
 * Permission ids must match server/config/menuPermissions.js (used to seed DB).
 */
import {
  buildEventsManagementPath,
  EVENT_MGMT_TAB_CLOSE_RECORD,
  EVENT_MGMT_TAB_EVENTS,
  EVENT_MGMT_TAB_PARTICIPANTS,
  EVENT_MGMT_TAB_PARTICIPANTS_DETAILS,
} from "../shared/utils/eventManagementTabs";

export const MENU_GROUPS = [
  {
    key: 'task',
    label: 'Tasks',
    children: [
      { path: '/Dashboard?view=simple', label: 'Task Dashboard', permissionId: 'menu.task_dashboard' },
      { path: '/Task', label: 'Task Management', permissionId: 'menu.task' },
      { path: '/TimeBlocking', label: 'Time Blocking', permissionId: 'menu.time_blocking' },
    ],
  },
  {
    key: 'financial',
    label: 'Finance',
    children: [
      { path: '/FinancialReports', label: 'Financial Reports', permissionId: 'menu.financial_reports' },
      { path: '/FinancialAccounts', label: 'Financial Accounts', permissionId: 'menu.financial_accounts' },
      { path: '/Transaction', label: 'Financial Transaction', permissionId: 'menu.transaction' },
    ],
  },
  {
    key: 'project',
    label: 'Projects',
    children: [
      { path: '/Dashboard', label: 'Project Dashboard', permissionId: 'menu.project_dashboard' },
      { path: '/Project', label: 'Project Management', permissionId: 'menu.project' },
      { path: '/FinancialInsights', label: 'Financial Insights', permissionId: 'menu.financial_insights' },
      { path: '/AssetManagement', label: 'Asset Management', permissionId: 'menu.asset_management' },
      { path: '/AssetManagement?tab=insurance', label: 'Insurance Management', permissionId: 'menu.insurance_management' },
      { path: '/AssetManagement?tab=vendors', label: 'Vendors & Ratings', permissionId: 'menu.vendor' },
      // Saved insights (Financial Insights) are added dynamically under Projects in App.js
    ],
  },
  {
    key: 'stockInvestment',
    label: 'Stock',
    children: [
      { path: '/StockManagement', label: 'Holdings', permissionId: 'menu.stock_management' },
      { path: '/StockManagement?tab=daily-news', label: 'Daily News', permissionId: 'menu.stock_management' },
      { path: '/StockManagement?tab=ark-buy', label: 'ARK ETF', permissionId: 'menu.stock_management' },
      { path: '/StockManagement?tab=buy-recommendation', label: 'Buy List', permissionId: 'menu.stock_management' },
    ],
  },
  {
    key: 'shieldCare',
    label: 'Shield Care',
    children: [
      { path: '/ShieldCare/Doctors', label: 'Doctors', permissionId: 'menu.shield_care_doctors' },
      { path: '/ShieldCare/Pharmacies', label: 'Pharmacies', permissionId: 'menu.shield_care_pharmacies' },
      { path: '/ShieldCare/Medications', label: 'Medications', permissionId: 'menu.shield_care_medications' },
      { path: '/ShieldCare/IntakeLogs', label: 'Intake Logs', permissionId: 'menu.shield_care_intake_logs' },
    ],
  },
  {
    key: 'community',
    label: 'Ops Plans',
    children: [
      { path: '/OpsPlanDashboard', label: 'Ops Plan Dashboard', permissionId: 'menu.ops_plan_dashboard' },
      { path: '/OperatingPlan?tab=aopByProgram', label: 'Ops Plan Management', permissionId: 'menu.operating_plan' },
      { path: '/ProgramManagement', label: 'Program Management', permissionId: 'menu.programs' },
    ],
  },
  {
    key: 'events',
    label: 'Events',
    children: [
      { path: '/EventDashboard', label: 'Event Dashboard', permissionId: 'menu.event_dashboard' },
      { path: '/EventCalendar', label: 'Event Calendar', permissionId: 'menu.event_calendar' },
      { path: buildEventsManagementPath({ tab: EVENT_MGMT_TAB_EVENTS }), label: 'Event Management', permissionId: 'menu.events' },
      { path: buildEventsManagementPath({ tab: EVENT_MGMT_TAB_PARTICIPANTS }), label: 'Event Participants', permissionId: 'menu.event_participants' },
      { path: buildEventsManagementPath({ tab: EVENT_MGMT_TAB_PARTICIPANTS_DETAILS }), label: 'Participant Profiles', permissionId: 'menu.event_participant_profiles' },
      { path: '/EventQRCheckIn', label: 'Event Check-In', permissionId: 'menu.event_qr_checkin' },
      { path: buildEventsManagementPath({ tab: EVENT_MGMT_TAB_CLOSE_RECORD }), label: 'Record & Close Event', permissionId: 'menu.close_event_record' },
    ],
  },
  {
    key: 'volunteering',
    label: 'Volunteering',
    children: [
      { path: '/VolunteerOpportunities', label: 'Volunteer Opportunities', permissionId: 'menu.volunteer_opportunities' },
      { path: '/VolunteerSignup', label: 'Volunteer Signup', permissionId: 'menu.volunteer_signup' },
      { path: '/VolunteerImpactDashboard', label: 'Volunteer Dashboard', permissionId: 'menu.volunteer_impact_dashboard' },
    ],
  },
  {
    key: 'surveys',
    label: 'Surveys',
    children: [
      { path: '/SurveyDashboard', label: 'Survey Dashboard', permissionId: 'menu.survey_dashboard' },
      { path: '/Surveys', label: 'Survey Management', permissionId: 'menu.surveys' },
      { path: '/SurveyInvitation', label: 'Survey Invitation', permissionId: 'menu.survey_dashboard' },
      { path: '/SurveyQuestionsInfo', label: 'Survey Questions Info', permissionId: 'menu.surveys' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    children: [
      { path: '/UserAdmin', label: 'User Admin', permissionId: 'menu.user_admin' },
      { path: '/Pricing', label: 'Pricing', permissionId: 'menu.pricing' },
      { path: '/Solutions', label: 'Solutions', permissionId: 'menu.solutions', superAdminOnly: true },
      { path: '/Register', label: 'Register Account', permissionId: 'menu.register', superAdminOnly: true },
      { path: '/AccountAdmin', label: 'Account Admin', permissionId: 'menu.account_admin' },
      { path: '/DataManagement', label: 'Data Management', permissionId: 'menu.data_management', superAdminOnly: true },
      { path: '/UserCustomizations', label: 'User customizations', permissionId: 'menu.user_customizations', superAdminOnly: true },
      { path: '/RolePermissionAdmin', label: 'Roles & Permissions', permissionId: 'menu.roles_permissions', superAdminOnly: true },
      { path: '/Communication', label: 'Communication', permissionId: 'menu.communication' },
      { path: '/StripeConnection', label: 'Stripe Connection', permissionId: 'menu.stripe_connection' },
    ],
  },
];

/** Path to permission id for route guards */
export function getPermissionIdByPath(path) {
  for (const group of MENU_GROUPS) {
    for (const item of group.children || []) {
      if (item.path && item.permissionId) {
        if (path === item.path || (item.path !== '/' && path.startsWith(item.path + '/'))) return item.permissionId;
      }
    }
  }
  return null;
}

/**
 * First menu path the user is allowed to access (by permission), in menu order.
 * Used as fallback when redirecting from a page they cannot access.
 * @param { (permissionId: string) => boolean } hasPermission
 * @param {{ isSuperAdmin?: boolean }} [options]
 * @returns { string } path or '/Task' if none
 */
export function getFirstPermittedPath(hasPermission, { isSuperAdmin = false } = {}) {
  if (typeof hasPermission !== 'function') return '/Task';
  for (const group of MENU_GROUPS) {
    for (const item of group.children || []) {
      if (
        item.path
        && item.permissionId
        && (!item.superAdminOnly || isSuperAdmin)
        && hasPermission(item.permissionId)
      ) {
        return item.path;
      }
    }
  }
  return '/Task';
}
